const topRightBottomLeft = {
    name: "top-right-bottom-left",
    path: "/top-right-bottom-left",
    title: "top-right-bottom-left",
    description: "Utilities for controlling the placement of positioned elements.",
    value: {
        'inset': {
            "inset-<number>": "calc(var(--1) * <number>)",
            "-inset-<number>": "calc(var(--1) * -<number>)",
            "inset-<fraction>": "calc(<fraction> * 100%)",
            "-inset-<fraction>": "calc(<fraction> * -100%)",
            "inset-px": "1px",
            "-inset-px": "-1px",
            "inset-full": "100%",
            "-inset-full": "-100%",
            "inset-auto": "auto",
            "inset-[<value>]": "<value>",
        },
        'inset-inline': {
            "inset-x-<number>": "calc(var(--1) * <number>)",
            "-inset-x-<number>": "calc(var(--1) * -<number>)",
            "inset-x-<fraction>": "calc(<fraction> * 100%)",  
            "-inset-x-<fraction>": "calc(<fraction> * -100%)",
            "inset-x-px": "1px",
            "-inset-x-px": "-1px",
            "inset-x-full": "100%",
            "-inset-x-full": "-100%",
            "inset-x-auto": "auto",
            "inset-x-[<value>]": "<value>",
        },
        'inset-inset-block': {
            "inset-y-<number>": "calc(var(--1) * <number>)",
            "-inset-y-<number>": "calc(var(--1) * -<number>)",
            "inset-y-<fraction>": "calc(<fraction> * 100%)",
            "-inset-y-<fraction>": "calc(<fraction> * -100%)",
            "inset-y-px": "1px",
            "-inset-y-px": "-1px",
            "inset-y-full": "100%",
            "-inset-y-full": "-100%",
            "inset-y-auto": "auto",
            "inset-y-[<value>]": "<value>",
        },
        'top':{
            "top-<number>": "calc(var(--1) * <number>)",
            "-top-<number>": "calc(var(--1) * -<number>)",
            "top-<fraction>": "calc(<fraction> * 100%)",
            "-top-<fraction>": "calc(<fraction> * -100%)",
            "top-px": "1px",
            "-top-px": "-1px",
            "top-full": "100%",
            "-top-full": "-100%",
            "top-auto": "auto",
            "top-[<value>]": "<value>", 
        },
        'right': {
            "right-<number>": "calc(var(--1) * <number>)",
            "-right-<number>": "calc(var(--1) * -<number>)",
            "right-<fraction>": "calc(<fraction> * 100%)",
            "-right-<fraction>": "calc(<fraction> * -100%)",
            "right-px": "1px",
            "-right-px": "-1px",
            "right-full": "100%",
            "-right-full": "-100%",
            "right-auto": "auto",
            "right-[<value>]": "<value>",
        },
        'bottom':{
            "bottom-<number>": "calc(var(--1) * <number>)",
            "-bottom-<number>": "calc(var(--1) * -<number>)",
            "bottom-<fraction>": "calc(<fraction> * 100%)",
            "-bottom-<fraction>": "calc(<fraction> * -100%)",
            "bottom-px": "1px",
            "-bottom-px": "-1px",
            "bottom-full": "100%",
            "-bottom-full": "-100%",
            "bottom-auto": "auto",
            "bottom-[<value>]": "<value>",
        },
        'left': {
            "left-<number>": "calc(var(--1) * <number>)",
            "-left-<number>": "calc(var(--1) * -<number>)",
            "left-<fraction>": "calc(<fraction> * 100%)",   
            "-left-<fraction>": "calc(<fraction> * -100%)",
            "left-px": "1px",
            "-left-px": "-1px",
            "left-full": "100%",
            "-left-full": "-100%",
            "left-auto": "auto",
            "left-[<value>]": "<value>",
        },

    },
    exampleData: [
        {
            "example": {
                "code": `
<!-- Pin to top left corner -->
<div class="relative size-32 ...">
  <div class="absolute top-0 left-0 size-16 ...">01</div>
</div>

<!-- Span top edge -->
<div class="relative size-32 ...">
  <div class="absolute inset-x-0 top-0 h-16 ...">02</div>
</div>

<!-- Pin to top right corner -->
<div class="relative size-32 ...">
  <div class="absolute top-0 right-0 size-16 ...">03</div>
</div>

<!-- Span left edge -->
<div class="relative size-32 ...">
  <div class="absolute inset-y-0 left-0 w-16 ...">04</div>
</div>

<!-- Fill entire parent -->
<div class="relative size-32 ...">
  <div class="absolute inset-0 ...">05</div>
</div>

<!-- Span right edge -->
<div class="relative size-32 ...">
  <div class="absolute inset-y-0 right-0 w-16 ...">06</div>
</div>

<!-- Pin to bottom left corner -->
<div class="relative size-32 ...">
  <div class="absolute bottom-0 left-0 size-16 ...">07</div>
</div>

<!-- Span bottom edge -->
<div class="relative size-32 ...">
  <div class="absolute inset-x-0 bottom-0 h-16 ...">08</div>
</div>

<!-- Pin to bottom right corner -->
<div class="relative size-32 ...">
  <div class="absolute right-0 bottom-0 size-16 ...">09</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="relative size-32 ...">
  <div class="absolute -top-4 -left-4 size-14 ..."></div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="inset-[3px] ...">
  <!-- ... -->
</div>
                `
            },
        },
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">top-<number>, right-<number>, bottom-<number>, left-<number>, inset-<number></code>
                  utilities like top-0 and bottom-4 to set the horizontal or vertical position of a positioned element:
            </p>

            <div class="grid grid-columns-3 grid-rows-3 place-items-center gap-4 font-mono text-sm leading-6 font-bold text-color-white">
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute top-0 left-0 flex size-16 items-center justify-center rounded-lg bg-color-violet-500 p-4">01</div>
                </div>
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute top-0 inset-x-0 flex h-16 items-center justify-center rounded-lg bg-color-violet-500 p-4">02</div>
                </div>
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute top-0 right-0 flex size-16 items-center justify-center rounded-lg bg-color-violet-500 p-4">03</div>
                </div>
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute inset-y-0 left-0 w-16 flex  items-center justify-center rounded-lg bg-color-violet-500 p-4">04</div>
                </div>
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute inset-0 flex  items-center justify-center rounded-lg bg-color-violet-500 p-4">05</div>
                </div>
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg  flex border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute inset-y-0 right-0 w-16 flex items-center justify-center rounded-lg bg-color-violet-500 p-4">06</div>
                </div>
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg  flex border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute bottom-0 left-0 size-16 flex items-center justify-center rounded-lg bg-color-violet-500 p-4">07</div>
                </div>
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg  flex border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute inset-x-0 bottom-0 h-16 flex items-center justify-center rounded-lg bg-color-violet-500 p-4">08</div>
                </div>
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg  flex border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute right-0 bottom-0 size-16 flex items-center justify-center rounded-lg bg-color-violet-500 p-4">09</div>
                </div>
            </div>            
            `
        },
         {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">Using negative values</code>
                  To use a negative top/right/bottom/left value, prefix the class name with a dash to convert it to a negative value:
            </p>

            <div class="font-mono text-sm leading-6 font-bold text-color-white mx-auto w-fit">
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute -top-4 -left-4 flex size-10 items-center justify-center rounded-lg bg-color-violet-500 p-4"></div>
                </div>
            </div>            
            `
        },
                 {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">inset-[<value>], top-[<value>]</code>
                 o set the position based on a completely custom value:
            </p>

            <div class="font-mono text-sm leading-6 font-bold text-color-white mx-auto w-fit">
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute inset-[3px] flex size-10 items-center justify-center rounded-lg bg-color-violet-500 p-4"></div>
                </div>
            </div>            
            `
        },
    ]
};

export default topRightBottomLeft;

<div className="sr-only">
    <div class="grid grid-columns-3 inset-[3px] w-16 h-16 top-0 w-fit right-0 inset-y-0 inset-x-0 grid-rows-3 place-items-center gap-4 font-mono text-sm leading-6 font-bold text-color-white">
                <div class="relative size-18 rounded-lg sm:size-32">
                    <div class="absolute inset-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="absolute top-0 left-0 -top-4 -left-4 flex size-10 items-center justify-center rounded-lg bg-color-violet-500 p-4">01</div>
                </div>
                
            </div>  
</div>